import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSession } from "../Login/SessionContext";
import { API_HOST } from "../../constants";
import { getCookie } from "../../utils/cookieUtil";
import Button from "../ui/Button";
import { isAdminUser } from "../../utils/userUtils";

function Comment({ comments, document_id, onSuccess }) {
  const { isLoading, user } = useSession();
  const [newComment, setNewComment] = useState({});
  const [editComment, setEditComment] = useState(null);
  const [replyComment, setReplyComment] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [lastCreatedId, setLastCreatedId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const topLevelComments = useMemo(
    () => comments.filter((comment) => comment.parent === null),
    [comments]
  );

  useEffect(() => {
    const initialState = {};
    topLevelComments.forEach((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        initialState[comment.id] = true;
      }
    });
    setExpandedReplies(initialState);
  }, [topLevelComments]);

  useEffect(() => {
    if (!lastCreatedId || comments.length === 0) return;
    const attemptScroll = (triesLeft) => {
      const el = document.querySelector(`[data-comment-id="${lastCreatedId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(lastCreatedId);
        setTimeout(() => setHighlightId(null), 2000);
        setLastCreatedId(null);
        return;
      }
      if (triesLeft > 0) {
        setTimeout(() => attemptScroll(triesLeft - 1), 120);
      }
    };
    attemptScroll(5);
  }, [lastCreatedId, comments]);

  if (isLoading && user) {
    return <div className="text-sm text-ink-200">댓글 로딩 중...</div>;
  }

  const handleAddComment = (parentId = null) => {
    if (!user) {
      alert("댓글 작성 권한이 없습니다.");
      return;
    }

    const content = parentId ? newComment[parentId] : newComment[document_id];
    if (!content) {
      alert("댓글 내용을 입력해주세요.");
      return;
    }

    const commentData = {
      bbs: document_id,
      author: user.id,
      content,
      parent: parentId || null,
    };

    axios
      .post(`${API_HOST}/api/comments/`, commentData, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        withCredentials: true,
      })
      .then((response) => {
        const createdId = response.data?.id;
        setLastCreatedId(createdId || null);
        setNewComment((prev) => ({ ...prev, [parentId || document_id]: "" }));
        setReplyComment(null);
        onSuccess();
      })
      .catch((error) => console.error("Error adding comment:", error.response?.data));
  };

  const handleEditComment = () => {
    if (!editComment?.content) {
      alert("댓글 내용을 입력해주세요.");
      return;
    }

    axios
      .put(`${API_HOST}/api/comments/${editComment.id}/`, editComment, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        withCredentials: true,
      })
      .then(() => {
        setEditComment(null);
        onSuccess();
      })
      .catch((error) => console.error("Error updating comment:", error.response?.data));
  };

  const handleDeleteComment = (commentId) => {
    if (!window.confirm("댓글을 삭제할까요?")) return;
    axios
      .delete(`${API_HOST}/api/comments/${commentId}/`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        withCredentials: true,
      })
      .then(() => {
        onSuccess();
      })
      .catch((error) => console.error("Error deleting comment:", error));
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">댓글 {comments.length}</h3>
          <p className="text-xs text-ink-200">도배, 욕설은 하지 말아주세요.</p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="btn-ghost"
        >
          {expanded ? "댓글 접기" : "댓글 펼치기"}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4">
          {topLevelComments.map((comment) => (
            <div
              key={comment.id}
              data-comment-id={comment.id}
              className={`rounded-2xl border p-4 transition ${
                highlightId === comment.id
                  ? "border-brand-400/60 bg-brand-400/10 shadow-glow"
                  : "border-white/10 bg-white/5"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-200">
                <span className="font-semibold text-white">{comment.author_nickname}</span>
                <span>
                  {new Date(comment.created_at).toLocaleDateString("ko-KR")} {" "}
                  {new Date(comment.created_at).toLocaleTimeString("ko-KR", { hour12: false })}
                </span>
              </div>

              <div className="mt-3 text-sm text-white">
                {editComment && editComment.id === comment.id ? (
                  <div className="space-y-3">
                    <textarea
                      className="soft-textarea"
                      value={editComment.content}
                      onChange={(e) =>
                        setEditComment((prev) => ({
                          ...prev,
                          content: e.target.value,
                        }))
                      }
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={handleEditComment}>등록</Button>
                      <Button variant="secondary" onClick={() => setEditComment(null)}>
                        취소
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p>{comment.content}</p>
                )}
              </div>

              <div className="mt-4 flex flex-wrap gap-3 text-xs text-ink-200">
                {(user?.id === comment.author || isAdminUser(user)) && (
                  <>
                    <button type="button" className="underline-offset-4 hover:underline" onClick={() => handleDeleteComment(comment.id)}>
                      삭제
                    </button>
                    <button
                      type="button"
                      className="underline-offset-4 hover:underline"
                      onClick={() => setEditComment({ ...comment, content: comment.content })}
                    >
                      수정
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="underline-offset-4 hover:underline"
                  onClick={() => setReplyComment(comment.id)}
                >
                  답글
                </button>
                {comment.replies && comment.replies.length > 0 && (
                  <button
                    type="button"
                    className="underline-offset-4 hover:underline"
                    onClick={() => toggleReplies(comment.id)}
                  >
                    답글 {expandedReplies[comment.id] ? "접기" : `보기 (${comment.replies.length})`}
                  </button>
                )}
              </div>

              {replyComment === comment.id && (
                <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-ink-900/60 p-3">
                  <textarea
                    className="soft-textarea"
                    placeholder={user ? "댓글을 입력하세요." : "로그인이 필요합니다."}
                    value={newComment[comment.id] || ""}
                    onChange={(e) => setNewComment((prev) => ({ ...prev, [comment.id]: e.target.value }))}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => handleAddComment(comment.id)}>등록</Button>
                    <Button variant="secondary" onClick={() => setReplyComment(null)}>
                      취소
                    </Button>
                  </div>
                </div>
              )}

              {comment.replies && expandedReplies[comment.id] && (
                <div className="mt-4 space-y-3 border-l border-white/10 pl-4">
                  {comment.replies.map((reply) => (
                    <div
                      key={reply.id}
                      data-comment-id={reply.id}
                      className={`rounded-xl border p-3 transition ${
                        highlightId === reply.id
                          ? "border-brand-400/60 bg-brand-400/10 shadow-glow"
                          : "border-white/10 bg-ink-900/60"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-200">
                        <span className="font-semibold text-white">{reply.author_nickname}</span>
                        <span>
                          {new Date(reply.created_at).toLocaleDateString("ko-KR")} {" "}
                          {new Date(reply.created_at).toLocaleTimeString("ko-KR", { hour12: false })}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-white">
                        {editComment && editComment.id === reply.id ? (
                          <div className="space-y-3">
                            <textarea
                              className="soft-textarea"
                              value={editComment.content}
                              onChange={(e) =>
                                setEditComment((prev) => ({
                                  ...prev,
                                  content: e.target.value,
                                }))
                              }
                            />
                            <div className="flex flex-wrap gap-2">
                              <Button onClick={handleEditComment}>등록</Button>
                              <Button variant="secondary" onClick={() => setEditComment(null)}>
                                취소
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p>{reply.content}</p>
                        )}
                      </div>
                      {(user?.id === reply.author || isAdminUser(user)) && (
                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-ink-200">
                          <button type="button" className="underline-offset-4 hover:underline" onClick={() => handleDeleteComment(reply.id)}>
                            삭제
                          </button>
                          <button
                            type="button"
                            className="underline-offset-4 hover:underline"
                            onClick={() => setEditComment({ ...reply, content: reply.content })}
                          >
                            수정
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="glass-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base font-semibold text-white">댓글 작성</h4>
            <p className="text-xs text-ink-200">도배, 욕설은 하지 말아주세요.</p>
          </div>
          {!user && <span className="pill">로그인 필요</span>}
        </div>
        <div className="mt-4 space-y-3">
          <textarea
            className="soft-textarea"
            placeholder={user ? "댓글을 입력하세요." : "로그인이 필요합니다."}
            value={newComment[document_id] || ""}
            onChange={(e) => setNewComment((prev) => ({ ...prev, [document_id]: e.target.value }))}
          />
          <div className="flex justify-end">
            <Button onClick={() => handleAddComment()}>등록</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Comment;
